import { apiFetch } from "./apiFetch";
import { invalidateScopedMeetingsCache } from "./partners";

export interface MeetingHostInfo {
  id: string;
  displayName: string;
  avatarUrl?: string;
}

/** One slot of a continuous room's day schedule. */
export interface ContinuousScheduleSlot {
  segmentId: string;
  start: string;
  end: string;
  hostId?: string;
  host?: MeetingHostInfo;
}

export interface VideoPlatformMeeting {
  id: string;
  externalMeetingId: string;
  title: string;
  description: string;
  status: string;
  type: string;
  createdBy: string;
  hostId: string;
  createdAt: string;
  meetingCreatedAt: string;
  /** Effective scheduled start (scheduledStartTime ?? startedAt ?? createdAt). */
  startDate?: string;
  /** Effective scheduled end (scheduledEndTime ?? endedAt). */
  endDate?: string;
  startedAt: string;
  endedAt: string;
  duration: number;
  durationMinutes: number;
  originalMeetingId: string;
  versionNumber: number;
  category?: string;
  isPublicVisible?: boolean;
  fallbackMeetUrl?: string;
  useFallback?: boolean;
  /** Resolved host (name/avatar) for the meeting's effective host id. */
  host?: MeetingHostInfo;
  /** Present on hourly segments grouped under a continuous room. */
  continuousRoomId?: string;
  /** True only on continuous room placeholders (the daily join target). */
  isContinuousRoom?: boolean;
  /** Continuous rooms only: the grouping key (e.g. "power-hour") — preferred group title. */
  continuousRoomKey?: string;
  /** Continuous rooms only: the day schedule, one slot per segment. */
  continuousSchedule?: ContinuousScheduleSlot[];
  /** Google Calendar event id backing this meeting, when it originates from GCal. */
  gcalEventId?: string;
  /** Recurring-series master id — shared by every occurrence of a recurring meeting. */
  recurringMeetingId?: string;
  /** Partner scope: when set, only that partner's members (plus Admins/Hosts)
   * can see and join this meeting. Null/absent = unscoped. */
  partnerId?: string | null;
}

/**
 * The STABLE key for a meeting, used to reference a session across occurrences.
 *
 * Prefer the recurring-series master id (so a whole series is one key), then the
 * GCal event id (a one-off occurrence), then the continuous-room grouping key
 * (a continuous room's placeholder id changes every day, so its stable identity
 * is continuousRoomKey — never its id), then the raw meeting id (a persistent
 * room or an ad-hoc meeting with no calendar backing). Never store a bare
 * per-occurrence/per-day id when a stable identity exists.
 */
export const stableSessionKey = (
  meeting: Pick<
    VideoPlatformMeeting,
    "id" | "gcalEventId" | "recurringMeetingId" | "continuousRoomKey"
  >,
): string =>
  meeting.recurringMeetingId ??
  meeting.gcalEventId ??
  meeting.continuousRoomKey ??
  meeting.id;

export interface MeetingCategoryOption {
  id: string;
  title: string;
}

export interface CapabilityControl {
  value: string;
  isLocked: boolean;
  lockedBy?: string;
  lockedAt?: string;
}

/** Per-attendee capability axes. */
export enum CapabilityAxis {
  Audio = "audio",
  Video = "video",
  Content = "content",
  Chat = "chat",
}

/** Top-level batch operations applied to every attendee in a meeting. */
export enum BatchCapabilityOperation {
  MuteAll = "mute_all",
  UnmuteAll = "unmute_all",
  DisableVideoAll = "disable_video_all",
  EnableVideoAll = "enable_video_all",
  DisableContentAll = "disable_content_all",
  EnableContentAll = "enable_content_all",
  MuteChatAll = "mute_chat_all",
  UnmuteChatAll = "unmute_chat_all",
}

/** Per-axis capability values used by the host UI when toggling locks. */
export enum CapabilityValue {
  /** Default unlocked state — the attendee controls the axis themselves. */
  SendReceive = "SendReceive",
  /** Locked state — disables and prevents the attendee from re-enabling. */
  None = "None",
}

export interface MeetingAttendee {
  id: string;
  externalMeetingId: string;
  attendeeId: string;
  userId: string;
  externalUserId: string;
  attendeeName: string;
  joinedAt: string;
  leftAt: string;
  isActive: boolean;
  capabilities: {
    audio: CapabilityControl;
    video: CapabilityControl;
    content: CapabilityControl;
    chat: CapabilityControl;
  };
  /** True when a MeetingKick row exists for this user in the meeting. */
  isBanned: boolean;
}

export interface Paging {
  count: number;
  total?: number;
  limit: number;
  nextToken?: string | null;
}

export interface MeetingsListResponse {
  data: VideoPlatformMeeting[];
  paging: Paging;
  filters?: Record<string, any>;
}

export interface AttendeesListResponse {
  data: MeetingAttendee[];
  paging: Paging;
}

// Short-lived getMeeting cache. get_meeting is a 2-hop (admin→video-platform)
// call that hits the video provider for Active meetings, and several detail
// sections read the same meeting at once (Google Meet fallback + Partner
// Access, in both the read-out and the edit dialog). The cache dedups those
// into ONE request per meeting and is invalidated on updateMeeting.
const meetingCache = new Map<
  string,
  { at: number; promise: Promise<VideoPlatformMeeting> }
>();
const MEETING_CACHE_TTL_MS = 15_000;
const invalidateMeetingCache = (id: string) => {
  meetingCache.delete(id);
};

/** A meeting's partner association only — served in-process by the admin API
 * (single AppSync read), much faster than the full 2-hop getMeeting. */
export interface MeetingPartnerAssociation {
  /** False when the meeting has no video-platform record yet (not synced). */
  found: boolean;
  partnerId: string | null;
}

// Same shape of cache as meetingCache: dedups the details read-out and the
// edit dialog into one request, invalidated when updateMeeting changes scope.
const partnerAssociationCache = new Map<
  string,
  { at: number; promise: Promise<MeetingPartnerAssociation> }
>();
const invalidatePartnerAssociationCache = (id: string) => {
  partnerAssociationCache.delete(id);
};

export const VideoPlatformResource = {
  getMeetings: (params?: {
    status?: string;
    createdBy?: string;
    startDate?: string;
    endDate?: string;
    limit?: number;
    nextToken?: string;
    prioritizeStatus?: string;
    sortBy?: string;
    sortOrder?: string;
    includeContinuousSegments?: boolean;
  }) =>
    apiFetch<MeetingsListResponse>("/admin/video-platform/meetings", {
      searchParams: params,
    }),

  getMeeting: (id: string) =>
    apiFetch<VideoPlatformMeeting>(`/admin/video-platform/meetings/${id}`),

  /** getMeeting with a short cache + in-flight dedup (invalidated on update). */
  getMeetingCached: (id: string): Promise<VideoPlatformMeeting> => {
    const hit = meetingCache.get(id);
    if (hit && Date.now() - hit.at < MEETING_CACHE_TTL_MS) return hit.promise;
    const promise = VideoPlatformResource.getMeeting(id);
    meetingCache.set(id, { at: Date.now(), promise });
    // Never cache a rejection — drop it so the next read retries.
    promise.catch(() => {
      if (meetingCache.get(id)?.promise === promise) meetingCache.delete(id);
    });
    return promise;
  },

  getMeetingPartnerAssociation: (id: string) =>
    apiFetch<MeetingPartnerAssociation>(
      `/admin/video-platform/meetings/${id}/partner-association`,
    ),

  /** getMeetingPartnerAssociation with a short cache + in-flight dedup
   * (invalidated on updateMeeting). */
  getMeetingPartnerAssociationCached: (
    id: string,
  ): Promise<MeetingPartnerAssociation> => {
    const hit = partnerAssociationCache.get(id);
    if (hit && Date.now() - hit.at < MEETING_CACHE_TTL_MS) return hit.promise;
    const promise = VideoPlatformResource.getMeetingPartnerAssociation(id);
    partnerAssociationCache.set(id, { at: Date.now(), promise });
    // Never cache a rejection — drop it so the next read retries.
    promise.catch(() => {
      if (partnerAssociationCache.get(id)?.promise === promise) {
        partnerAssociationCache.delete(id);
      }
    });
    return promise;
  },

  createMeeting: (data: Record<string, any>) =>
    apiFetch<VideoPlatformMeeting>("/admin/video-platform/meetings", {
      method: "POST",
      body: data,
    }),

  deleteMeeting: (id: string) =>
    apiFetch<VideoPlatformMeeting>(`/admin/video-platform/meetings/${id}`, {
      method: "DELETE",
    }),

  getMeetingAttendees: (params: {
    meetingId: string;
    active?: boolean;
    limit?: number;
    nextToken?: string;
  }) => {
    const { meetingId, ...searchParams } = params;
    return apiFetch<AttendeesListResponse>(
      `/admin/video-platform/meetings/${meetingId}/attendees`,
      { searchParams },
    );
  },

  updateAttendeeCapabilities: (
    meetingId: string,
    attendeeId: string,
    capabilities: {
      audio?: { value?: string; lock?: boolean };
      video?: { value?: string; lock?: boolean };
      content?: { value?: string; lock?: boolean };
      chat?: { value?: string; lock?: boolean };
    },
  ) =>
    apiFetch<{
      attendeeId: string;
      userId: string;
      audioCapability: CapabilityControl;
      videoCapability: CapabilityControl;
      contentCapability: CapabilityControl;
      chatCapability: CapabilityControl;
    }>(
      `/admin/video-platform/meetings/${meetingId}/attendees/${attendeeId}/capabilities`,
      { method: "PATCH", body: capabilities },
    ),

  batchUpdateAttendeeCapabilities: (
    meetingId: string,
    body: {
      operation: BatchCapabilityOperation;
      excludeHosts: boolean;
      lock: boolean;
    },
  ) =>
    apiFetch<{ updatedCount: number; attendeeIds: string[] }>(
      `/admin/video-platform/meetings/${meetingId}/attendees/batch`,
      { method: "PATCH", body },
    ),

  /** Ban (kick) an attendee from a meeting — admin-only. */
  banAttendee: (meetingId: string, attendeeId: string, reason?: string) =>
    apiFetch<{
      meetingId: string;
      kickedUserId: string;
      alreadyKicked: boolean;
    }>(
      `/admin/video-platform/meetings/${meetingId}/attendees/${attendeeId}/kick`,
      { method: "POST", body: reason ? { reason } : {} },
    ),

  /** Lift a ban (remove the kick row) so the user can rejoin. Idempotent. */
  unbanAttendee: (meetingId: string, attendeeId: string) =>
    apiFetch<{
      meetingId: string;
      unbannedUserId: string;
      wasBanned: boolean;
    }>(
      `/admin/video-platform/meetings/${meetingId}/attendees/${attendeeId}/kick`,
      { method: "DELETE" },
    ),

  getCategories: () =>
    apiFetch<{ categories: MeetingCategoryOption[] }>(
      "/admin/video-platform/meetings/categories",
    ),

  updateMeeting: (
    id: string,
    data: {
      category?: string;
      title?: string;
      description?: string;
      isPublicVisible?: boolean;
      useFallback?: boolean;
      fallbackMeetUrl?: string | null;
      /** Partner scope: a partner id restricts visibility+join to that partner's
       * members; null clears it. Absent = unchanged. */
      partnerId?: string | null;
    },
  ) =>
    apiFetch<{
      id: string;
      externalMeetingId: string;
      title: string;
      category?: string;
      status: string;
      isPublicVisible?: boolean;
    }>(`/admin/video-platform/meetings/${id}`, {
      method: "PATCH",
      body: data,
    }).then((r) => {
      invalidateMeetingCache(id);
      invalidatePartnerAssociationCache(id);
      // Partner scope may have changed — the per-partner scoped-meetings
      // caches (filter/badges) can't know which owner changed, so drop all.
      if (data.partnerId !== undefined) {
        invalidateScopedMeetingsCache();
      }
      return r;
    }),
};
